import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { fetchAllRowsServer, serializeConditionsToParams } from '@resource-kit/controllers';
import { useLocalList } from '@resource-kit/controllers/react';
import { ListView, createColumns } from '@resource-kit/ui/react';
import { queryKeys } from '@lib/queryKeys';
import { FULFILLMENT_JOB_SEARCH } from '@/services/filters.generated';
import { buildShippingDateConditions, jobResource } from '../jobResource';

/** The backend list kernel caps a page at 100 rows; a larger page is cut silently. */
const PAGE_SIZE = 100;

/**
 * The list view of `/dispatch` (#2516): the selected shipping date's unfinished
 * delivery jobs, under the same header and range label as the board and the map.
 *
 * Not an embedded `ResourcePage`: that one owns its list state in the URL, and
 * `/dispatch` already carries the bare `date` and `tab` keys, which the
 * resource list would read as filters, drop and warn about — and its own
 * filters would land in the dispatch URL. So the day is read in full here and
 * `useLocalList` pages, sorts and searches it in memory, never touching the
 * URL. Read-only: selection, bulk commands and printing stay on the
 * "Delivery jobs" page.
 */
export function DispatchJobList({ date }: { date: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Under `dispatchJobs`, so a dispatch run's invalidation refreshes it too.
  const query = useQuery({
    queryKey: queryKeys.dispatchJobs.list({ scope: 'dispatch-day', date }),
    queryFn: () =>
      fetchAllRowsServer(
        jobResource.host.api.list,
        {
          page: 1,
          limit: PAGE_SIZE,
          sort: 'created_at:desc',
          extraParams: serializeConditionsToParams(buildShippingDateConditions(date), {}),
        },
        { pageSize: PAGE_SIZE },
      ),
    staleTime: 60_000,
  });

  const columns = useMemo(() => createColumns(jobResource, { mode: 'list' }), []);

  const list = useLocalList({
    rows: query.data ?? [],
    // The same columns the jobs route searches, so the jobs page's search
    // placeholder holds here. This search is in memory (`matchesSearch`, a raw
    // substring), so a phone matches only as typed: the server strips its
    // format characters (#2678), this list does not. Known v1 imprecision.
    searchFields: FULFILLMENT_JOB_SEARCH,
    // The date is fixed by the page and the list is read-only: no filter builder.
    capabilities: { filter: false },
    defaultSort: 'created_at:desc',
    limit: 50,
    status: query.isPending ? 'loading' : query.isError ? 'error' : 'ready',
    error: query.error?.message ?? null,
    onRetry: () => void query.refetch(),
  });

  const searchPlaceholder = jobResource.host.searchPlaceholder;

  return (
    <section data-testid="dispatch-job-list" data-date={date}>
      {/*
        The props mirror `ResourcePage`'s `<ListView>` wiring on purpose,
        minus what this view does not offer. `capabilities={{ filter: false }}`
        is explicit because `ListView` reads an absent `filter` as `true`. No
        `fields` / `queryBuilderColumns` / `filterable`: they only feed the
        filter builder, which is off. No `emptyAction` (nothing to create
        here), no selection, export or print (read-only). `preferenceKey`
        is the jobs page's, so the two lists share column visibility and
        the table/cards layout.
      */}
      <ListView
        columns={columns}
        rows={list.rows}
        pagination={list.pagination}
        status={list.status}
        error={list.error}
        onRetry={list.retry}
        isFiltered={list.isFiltered}
        onClearFilters={list.clearFilters}
        droppedFilters={list.droppedFilters}
        mergeNotices={list.mergeNotices}
        onPageChange={list.setPage}
        onLimitChange={list.setLimit}
        onSortChange={list.setSort}
        onSearchChange={list.setSearch}
        searchable={list.capabilities.search}
        capabilities={{ filter: false }}
        sortField={list.sortField}
        sortOrder={list.sortOrder}
        search={list.search}
        conditions={list.conditions}
        searchPlaceholder={
          searchPlaceholder ? t(searchPlaceholder, { defaultValue: searchPlaceholder }) : undefined
        }
        // The resource's own detail route; ResourcePage's default would build
        // `/dispatch/<id>` from this page's path.
        onRowClick={(row) => navigate(`/dispatch/jobs/${encodeURIComponent(row.id)}`)}
        getRowHref={(row) => `/dispatch/jobs/${encodeURIComponent(row.id)}`}
        getRowId={(row) => row.id}
        exportFileName={jobResource.host.slug}
        // Not reached through `ResourcePage`, so the jobs host's card and
        // preference key are forwarded by hand: without the key this list
        // would keep the old `dispatch/jobs` preferences while the jobs page
        // reads `dispatch/jobs.v2`.
        renderCard={jobResource.host.listCard}
        preferenceKey={jobResource.host.listPreferenceKey}
      />
    </section>
  );
}
